/**
 * User and tenant operations for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class UserManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listUsers(
    page = 1,
    size = 20,
    name?: string,
    tenantId?: number,
  ): Promise<Record<string, any>> {
    const options: Record<string, string | number> = {};
    if (name) options.name = name;
    if (tenantId !== undefined) options.tenantId = tenantId;
    const params: Record<string, any> = { current: page, size };
    if (Object.keys(options).length) params.options = JSON.stringify(options);
    return this.session.get('/api/system/user/page', { params });
  }

  async getUser(userId: number): Promise<Record<string, any>> {
    return this.session.get(`/api/system/user/${userId}`);
  }

  async getCurrentUser(): Promise<Record<string, any>> {
    return this.session.get('/api/system/user/current');
  }

  async createUser(
    username: string,
    password: string,
    name: string,
    email?: string,
    phone?: string,
    tenantId?: number,
    roles?: number[],
  ): Promise<Record<string, any>> {
    const data = {
      data: {
        username,
        password,
        name,
        email,
        phone,
        tenantId,
        roleIds: roles ?? [],
      },
    };
    return this.session.post('/api/system/user', { data });
  }

  async updateUser(
    userId: number,
    name?: string,
    email?: string,
    phone?: string,
    roles?: number[],
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { id: userId } };
    if (name) data.data.name = name;
    if (email) data.data.email = email;
    if (phone) data.data.phone = phone;
    if (roles) data.data.roleIds = roles;
    return this.session.put('/api/system/user', { data });
  }

  async deleteUser(userId: number): Promise<Record<string, any>> {
    return this.session.delete(`/api/system/user/${userId}`);
  }

  async changePassword(
    userId: number,
    oldPassword: string,
    newPassword: string,
  ): Promise<Record<string, any>> {
    return this.session.post('/api/system/user/changePassword', {
      data: { userId, oldPassword, newPassword },
    });
  }

  async resetPassword(userId: number, newPassword: string): Promise<Record<string, any>> {
    return this.session.post('/api/system/user/resetPassword', {
      data: { userId, newPassword },
    });
  }

  async listRoles(page = 1, size = 20, name?: string): Promise<Record<string, any>> {
    const options: Record<string, string> = {};
    if (name) options.name = name;
    const params: Record<string, any> = { current: page, size };
    if (Object.keys(options).length) params.options = JSON.stringify(options);
    return this.session.get('/api/system/role/page', { params });
  }

  async getRole(roleId: number): Promise<Record<string, any>> {
    return this.session.get(`/api/system/role/${roleId}`);
  }

  async createRole(
    name: string,
    code: string,
    description?: string,
    permissions?: string[],
  ): Promise<Record<string, any>> {
    const data = {
      data: {
        name,
        code,
        description,
        permissions: permissions ?? [],
      },
    };
    return this.session.post('/api/system/role', { data });
  }

  async updateRole(
    roleId: number,
    name?: string,
    description?: string,
    permissions?: string[],
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { id: roleId } };
    if (name) data.data.name = name;
    if (description) data.data.description = description;
    if (permissions) data.data.permissions = permissions;
    return this.session.put('/api/system/role', { data });
  }

  async deleteRole(roleId: number): Promise<Record<string, any>> {
    return this.session.delete(`/api/system/role/${roleId}`);
  }

  async listTenants(page = 1, size = 20, name?: string): Promise<Record<string, any>> {
    const options: Record<string, string> = {};
    if (name) options.name = name;
    const params: Record<string, any> = { current: page, size };
    if (Object.keys(options).length) params.options = JSON.stringify(options);
    return this.session.get('/api/cdp/tenant/page', { params });
  }

  async getTenant(tenantId: number): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/tenant/${tenantId}`);
  }

  async createTenant(
    name: string,
    code: string,
    description?: string,
    adminUsername?: string,
    adminPassword?: string,
  ): Promise<Record<string, any>> {
    const data = {
      data: {
        name,
        code,
        description,
        adminUsername,
        adminPassword,
      },
    };
    return this.session.post('/api/cdp/tenant', { data });
  }

  async updateTenant(
    tenantId: number,
    name?: string,
    description?: string,
    enabled?: boolean,
  ): Promise<Record<string, any>> {
    const data: Record<string, any> = { data: { id: tenantId } };
    if (name) data.data.name = name;
    if (description) data.data.description = description;
    if (enabled !== undefined) data.data.enabled = enabled ? 1 : 0;
    return this.session.put('/api/cdp/tenant', { data });
  }

  async deleteTenant(tenantId: number): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/tenant/${tenantId}`);
  }

  async switchTenant(tenantId: number): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/tenant/switch/${tenantId}`);
  }

  async getUserPermissions(): Promise<string[]> {
    const result = await this.session.get('/api/system/user/permissions');
    return result?.data ?? [];
  }

  async getUserProjects(page = 1, size = 20): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/sys_project/extend/getEditableProjects', {
      params: { current: page, size },
    });
  }

  async syncUsersFromExternal(
    source: string,
    config: Record<string, any>,
  ): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/user/sync', { data: { source, config } });
  }

  async getSyncStatus(syncId: string): Promise<Record<string, any>> {
    return this.session.get(`/api/cdp/user/sync/${syncId}/status`);
  }

  // --- Extended user APIs ---

  async getAuthInfo(): Promise<Record<string, any>> {
    return this.session.get('/api/auth/info');
  }

  async getUserConfig(): Promise<Record<string, any>> {
    return this.session.get('/api/system/users/config');
  }

  async saveUserConfig(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/system/users/config/save', { data });
  }

  async deleteUserConfig(configId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/system/users/config/delete/${configId}`);
  }

  async getFavoriteColors(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/user/config/getFavoriteColors');
  }

  async addFavoriteColor(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/user/config/addFavoriteColor', { data });
  }

  async removeFavoriteColor(colorId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/cdp/user/config/removeFavoriteColor/${colorId}`);
  }

  async getMyTheme(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/user/config/getMyTheme');
  }

  async createUserExtend(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/system/users', { data });
  }

  async deleteUserExtend(userId: string): Promise<Record<string, any>> {
    return this.session.delete(`/api/system/users/${userId}`);
  }

  async listRolesExtend(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/system/roles', { params });
  }
}
